import { FC } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DailyNavNewsItem } from 'api';
import { formatProviderCode, shortDate } from 'utilities';
import { NewsDialogProps } from './NewsDialog.props';

const itemCaption = (item: DailyNavNewsItem): string => {
  const publication = item.source_links.find(link => link.publication)?.publication;
  const provider = item.source?.provider_code ? formatProviderCode(item.source.provider_code) : null;
  return [publication, provider].filter(Boolean).join(' · ');
};

const NewsDialog: FC<NewsDialogProps> = ({ row, open, onClose }) => {
  const items = row?.record.secondary_breakdown?.news ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth id="news-dialog" data-testid="news-dialog">
      <DialogTitle>
        <Typography variant="h6">News{row?.companyName ? ` — ${row.companyName}` : ''}</Typography>
      </DialogTitle>
      <DialogContent>
        {items.length === 0 && <Alert severity="info">No news is available for this row.</Alert>}
        {items.length > 0 && (
          <Table size="small" data-testid="news-table">
            <TableHead>
              <TableRow>
                <TableCell>Article</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const url = item.source_links.find(link => link.url)?.url;
                const date = shortDate(item.published_at) ?? item.published_at.slice(0, 10);
                return (
                  <TableRow key={`${item.published_at}-${item.title}-${index}`}>
                    <TableCell>
                      <Typography variant="body2">
                        {date}
                        {' — '}
                        {url ? (
                          <Link href={url} target="_blank" rel="noopener noreferrer">
                            {item.title}
                          </Link>
                        ) : (
                          item.title
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {itemCaption(item) || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} id="news-dialog-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsDialog;
